import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  ArrowRight,
  Play,
  Check,
  Sparkles,
  BookOpen,
  Clock,
  X,
  ChevronDown,
  Award,
  Users,
  Globe,
  RefreshCw,
  Send,
  ArrowUpRight
} from "lucide-react";
import "@/App.css";

// Unsplash premium curated lifestyle and parenting images
const IMAGES = {
  hero: "https://tagmango.com/assets/chatgpt-image-sep-22-2026-054033-pm-d544a3681d834d6512b45d772fe8ba3a.png",
  manifesto1: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=600",
  manifesto2: "https://images.unsplash.com/photo-1516627145497-ae6968895b74?auto=format&fit=crop&q=80&w=600",
  manifesto3: "https://images.unsplash.com/photo-1543269664-76bc3997d9ea?auto=format&fit=crop&q=80&w=600",
  manifesto4: "https://images.unsplash.com/photo-1536640712-4d4c36ff0e4e?auto=format&fit=crop&q=80&w=600",
  author: "https://tagmango.com/assets/my-image-275849a5cdb3f3c19c3b79772a734541.png",
  logo: "https://tagmango.com/assets/logo-with-transperent-background-741dc783b2b83032ee1088d6d3feda50.png",
  ebook: "https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?auto=format&fit=crop&q=80&w=600"
};

export default function App() {
  // Smooth Momentum Scrolling with Lenis
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  // 15-Minute Resetting Cohort Countdown Timer
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
  useEffect(() => {
    const storedStart = localStorage.getItem("countdown_start_time");
    let startTime = Date.now();

    if (storedStart) {
      const elapsed = Math.floor((Date.now() - parseInt(storedStart, 10)) / 1000);
      if (elapsed < 900) {
        setTimeLeft(900 - elapsed);
        startTime = parseInt(storedStart, 10);
      } else {
        localStorage.setItem("countdown_start_time", Date.now().toString());
        setTimeLeft(900);
      }
    } else {
      localStorage.setItem("countdown_start_time", Date.now().toString());
    }

    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      if (elapsed >= 900) {
        startTime = Date.now();
        localStorage.setItem("countdown_start_time", startTime.toString());
        setTimeLeft(900);
      } else {
        setTimeLeft(900 - elapsed);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // 5-Second Delayed eBook Popup Modal
  const [showEbook, setShowEbook] = useState(false);
  const [ebookName, setEbookName] = useState("");
  const [ebookEmail, setEbookEmail] = useState("");
  const [ebookPhone, setEbookPhone] = useState("");
  const [ebookSubmitted, setEbookSubmitted] = useState(false);

  // Footer Newsletter states
  const [footerName, setFooterName] = useState("");
  const [footerEmail, setFooterEmail] = useState("");
  const [footerPhone, setFooterPhone] = useState("");
  const [footerSubmitted, setFooterSubmitted] = useState(false);



  // WhatsApp Community checked state
  const [whatsappAgreed, setWhatsappAgreed] = useState(false);
  const [showWhatsappWarning, setShowWhatsappWarning] = useState(false);

  const handleWhatsappClick = (e) => {
    if (!whatsappAgreed) {
      e.preventDefault();
      setShowWhatsappWarning(true);
    }
  };

  useEffect(() => {
    const closed = sessionStorage.getItem("ebook_popup_closed");
    if (!closed) {
      const timer = setTimeout(() => {
        setShowEbook(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const closeEbook = () => {
    setShowEbook(false);
    sessionStorage.setItem("ebook_popup_closed", "true");
  };

  const handleEbookSubmit = (e) => {
    e.preventDefault();
    if (ebookEmail && ebookName) {
      setEbookSubmitted(true);
      setTimeout(() => {
        closeEbook();
      }, 2500);
    }
  };

  // Self-Leadership Archetype Quiz state
  const [quizStep, setQuizStep] = useState(0); // 0 = intro, 1, 2, 3 = questions, 4 = result
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);

  const quizQuestions = [
    {
      q: "How do you respond when your child exhibits intense emotional distress or a temper tantrum?",
      options: [
        { text: "React immediately with frustration or demanding compliance.", value: "reactive" },
        { text: "Pause, anchor your breathing, and co-regulate with deep composure.", value: "conscious" },
        { text: "Analyse the behavior, researching rules and optimal structures.", value: "analytical" }
      ]
    },
    {
      q: "What does your family's primary morning starting routine look like?",
      options: [
        { text: "Hurrying, rushing through schedules, and preparing for the rush.", value: "reactive" },
        { text: "Spiritual space: gratitude, quiet connection, or soft chanting.", value: "conscious" },
        { text: "Checking checklists, morning schedules, and school notifications.", value: "analytical" }
      ]
    },
    {
      q: "How do you balance structural guidelines with your child's emotional freedom?",
      options: [
        { text: "Enforce strict compliance, sometimes sacrificing connection for order.", value: "reactive" },
        { text: "Nurture authentic connection, protecting space for gentle expression.", value: "conscious" },
        { text: "Optimize parenting tools and cognitive strategies to find the balance.", value: "analytical" }
      ]
    }
  ];

  const handleQuizAnswer = (value) => {
    const nextAnswers = [...quizAnswers, value];
    setQuizAnswers(nextAnswers);
    if (quizStep < 3) {
      setQuizStep(quizStep + 1);
    } else {
      // Calculate archetype based on frequency
      const counts = nextAnswers.reduce((acc, curr) => {
        acc[curr] = (acc[curr] || 0) + 1;
        return acc;
      }, {});
      const topVal = Object.keys(counts).reduce((a, b) => counts[a] > counts[b] ? a : b);

      let archetype = {
        title: "The Mindful Anchor",
        badge: "Spiritual Guide",
        desc: "You possess deep self-awareness and emotional composure as a parent. You connect before correcting and guide from a place of peace, but would benefit from joining a supportive community of like-minded families.",
        quote: "True parenting is the space between a child's stimulus and your response.",
        advise: "Focus on establishing beautiful daily family rituals and participating in weekly masterminds."
      };
      if (topVal === "reactive") {
        archetype = {
          title: "The High-Velocity Protector",
          badge: "Protective Shield",
          desc: "You are exceptionally loving and active, but highly prone to parenting fatigue and stress-induced reactivity. You need to engineer intentional pauses and spiritual coregulation to lead without burnout.",
          quote: "Calm is a superpower you give your child.",
          advise: "Focus on daily meditation, breathwork, and gentle connection workshops in our mastermind."
        };
      } else if (topVal === "analytical") {
        archetype = {
          title: "The Structured Architect",
          badge: "Nurturing Blueprint Designer",
          desc: "You are highly analytical and structure-driven, searching for the perfect parenting formula. Your blocker is over-intellectualizing emotional moments; you need to transition from rules to raw spiritual connection.",
          quote: "Children need your presence, not your perfection.",
          advise: "Focus on heart-led communication templates and active peer cohorts."
        };
      }
      setQuizResult(archetype);
      setQuizStep(4);
    }
  };

  const restartQuiz = () => {
    setQuizStep(0);
    setQuizAnswers([]);
    setQuizResult(null);
  };

  // Accordion FAQs state
  const [faqOpen, setFaqOpen] = useState(null);
  const faqs = [
    {
      q: "Who is the God Conscious Parenting Hub designed for?",
      a: "The Hub is designed for mindful parents, educators, and spiritual seekers who want to raise resilient, peaceful, and spiritually grounded children in a distracted modern world."
    },
    {
      q: "What is the core distinction between the Seedling and Divine tiers?",
      a: "The Seedling Path offers self-paced foundational courses and ritual guides. The Divine Mastermind is our high-touch experience with live group coaching, peer sharing, and personalized archetype reviews."
    },
    {
      q: "How much daily commitment is required?",
      a: "Implementing our morning gratitude rituals and bedtime connection stories takes less than 15 minutes a day, designed to blend into busy family schedules."
    },
    {
      q: "Will I have direct access to Swayamprava?",
      a: "Yes! Divine Mastermind members attend live bi-weekly spiritual circles and interactive Q&A workshops with Swayamprava."
    },
    {
      q: "How does the resetting countdown work?",
      a: "To ensure maximum focus, cohort admissions operate in intimate, highly synchronized blocks. If you register before the dynamic timer closes, you immediately unlock our signature 'Spiritual Bedtime Stories' digital playbook as a complimentary bonus."
    }
  ];

  // Billing Cycle state (Monthly vs Annual)
  const [billingPeriod, setBillingPeriod] = useState("annual"); // "monthly" or "annual"

  // Spotlight card mouse tracking
  const spotlightRef = useRef(null);
  const handleSpotlightMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    card.style.setProperty("--x", `${x}px`);
    card.style.setProperty("--y", `${y}px`);
  };

  // Parallax Hero Mouse Tilt
  const heroCardRef = useRef(null);
  const handleHeroMouseMove = (e) => {
    const card = heroCardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left - width / 2;
    const mouseY = e.clientY - rect.top - height / 2;
    const rX = (mouseY / height) * -12;
    const rY = (mouseX / width) * 12;
    card.style.transform = `perspective(1000px) rotateX(${rX}deg) rotateY(${rY}deg) scale3d(1.02, 1.02, 1.02)`;
  };

  const handleHeroMouseLeave = () => {
    const card = heroCardRef.current;
    if (!card) return;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if visitor was redirected after successful masterclass registration
  const [showThankYouPage, setShowThankYouPage] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("registered") === "true" || params.get("status") === "success" || params.get("status") === "registered") {
      setShowThankYouPage(true);
    }
  }, []);

  if (showThankYouPage) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] text-foreground font-sans relative flex flex-col items-center justify-center p-6 overflow-hidden">
        {/* Editorial noise overlay */}
        <div className="absolute inset-0 bg-noise pointer-events-none opacity-[0.03]" />
        
        {/* Content Box */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="thank-you-page"
          className="max-w-md w-full bg-background rounded-[2.5rem] border border-foreground/5 shadow-2xl p-8 sm:p-10 text-center flex flex-col items-center relative z-10 mx-auto"
        >
          {/* Logo / Brand Name */}
          <div className="mb-8">
            <span className="font-serif text-lg font-bold tracking-tight text-primary block">God Conscious Parenting Hub</span>
          </div>

          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6 border border-primary/20">
            <Check className="w-8 h-8 text-primary" />
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl font-light text-foreground mb-4">
            Thank You for Registering! 🎉
          </h1>

          <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6">
            Your registration for the God Conscious Parenting Masterclass is confirmed. You have taken a beautiful first step toward creating a calmer, happier and more connected family.
          </p>

          <div className="p-5 rounded-2xl bg-primary/5 border border-primary/10 mb-8 text-left text-xs text-muted-foreground/90 leading-relaxed w-full">
            <strong className="text-foreground block mb-1">Important instruction:</strong>
            Please join our private WhatsApp community to receive resources, masterclass reminders, and dates.
          </div>

          <div className="flex flex-col gap-4 w-full">
            <a
              href="https://chat.whatsapp.com/IiyyqE0a9uWIbNOJ769le7"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="modal-success-whatsapp-button"
              className="w-full py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase text-center flex items-center justify-center gap-2 shadow-lg shadow-primary/15 transition-all duration-300"
            >
              Join the WhatsApp Community
              <ArrowUpRight className="w-4 h-4" />
            </a>

            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                setShowThankYouPage(false);
                window.history.pushState({}, "", "/");
              }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors uppercase tracking-widest font-semibold mt-4"
            >
              Back to Home
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary/20 selection:text-foreground relative overflow-x-hidden noise-bg">
      
      {/* Dynamic Cohort Sticky Countdown Banner */}
      <div className="bg-primary text-primary-foreground py-2.5 px-4 text-center text-xs font-semibold tracking-wider uppercase flex items-center justify-center gap-3 relative z-50">
        <Sparkles className="w-4 h-4 animate-pulse text-accent" />
        <span>Divine cohort applications closing in</span>
        <span data-testid="cohort-countdown-timer" className="font-mono text-sm px-2 py-0.5 rounded bg-foreground/15 border border-primary-foreground/10">
          {formatTime(timeLeft)}
        </span>
        <span className="hidden sm:inline">• Unlock our &apos;Spiritual Bedtime Stories&apos; playbook today</span>
      </div>

      {/* FIXED GLASSMOPRHIC NAVIGATION */}
      <header data-testid="header-container" className="sticky top-0 left-0 right-0 z-40 bg-[#F9F8F6] md:bg-[#F9F8F6]/85 backdrop-blur-xl border-b border-stone-200 py-4 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between">
          
          {/* Logo & Brand Identity */}
          <a href="#home" data-testid="nav-brand-logo" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center border border-foreground/10 group-hover:scale-105 transition-transform duration-300 overflow-hidden">
              <img src={IMAGES.logo} className="w-full h-full object-contain" alt="God Conscious Parenting Logo" />
            </div>
            <div className="flex flex-col text-left">
              <span className="font-serif text-lg font-bold tracking-tight text-foreground leading-tight">God Conscious Parenting Hub</span>
              <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-widest leading-none">by Swayamprava</span>
            </div>
          </a>

          {/* Desktop Nav Links */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#home" data-testid="nav-link-home" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              Home
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#manifesto" data-testid="nav-link-manifesto" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              Manifesto
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#quiz" data-testid="nav-link-quiz" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              Archetype Quiz
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#membership" data-testid="nav-link-membership" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              Platinum Membership
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#community" data-testid="nav-link-community" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              Community
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>

            <a href="#faq" data-testid="nav-link-faq" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              FAQ
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="https://learning.godconsciousparenting.com/web/checkout/6a9d08752dcaa6295db3b498"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-join-button"
              className="px-6 py-2.5 rounded-full bg-foreground hover:bg-foreground/90 text-background text-xs font-bold tracking-wider uppercase flex items-center justify-center gap-2 hover:gap-3 transition-all duration-300"
            >
              Free Master Class <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 rounded-lg text-foreground hover:text-primary transition-colors focus:outline-none"
            aria-label="Open Menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

      </header>

      {/* Mobile Navigation Panel with Framer Motion */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-50 bg-[#F9F8F6] p-6 flex flex-col justify-between md:hidden"
          >
            <div className="flex flex-col gap-6 text-left">
              <div className="flex items-center justify-between">
                <span className="font-serif text-xl font-bold tracking-tight">God Conscious Parenting Hub</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="nav-mobile-close"
                  className="p-2 text-foreground hover:text-primary transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <nav className="flex flex-col gap-5 mt-8 text-2xl font-serif font-light">
                <a href="#home" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">Home</a>
                <a href="#manifesto" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">Manifesto</a>
                <a href="#quiz" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">Archetype Quiz</a>
                <a href="#membership" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">Platinum Membership</a>
                <a href="#community" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">Community</a>
                <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">FAQ</a>
              </nav>
            </div>

            <div className="flex flex-col gap-4 mt-auto">
              <a
                href="https://learning.godconsciousparenting.com/web/checkout/6a9d08752dcaa6295db3b498"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full py-4 rounded-full bg-foreground text-background text-center font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-2"
              >
                Join Free Master Class <ArrowRight className="w-4 h-4" />
              </a>
              <p className="text-center text-xs text-muted-foreground font-medium">Cohort admissions close soon</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main>
        
        {/* HERO SECTION WITH LARGE KINETIC MASK REVEAL */}
        <section id="home" className="relative min-h-screen bg-background pt-16 pb-20 md:py-32 flex items-center overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Content Column */}
            <div className="lg:col-span-7 flex flex-col items-start text-left">
              
              {/* Dynamic badge with signature reveal */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent border border-foreground/5 shadow-sm mb-6"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse"></span>
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  Join the Global Mastermind Cohort
                </span>
              </motion.div>

              {/* Kinetic Header Mask Reveal */}
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-foreground leading-[1.1] mb-6">
                <span className="mask-reveal">
                  <motion.span
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                    className="block"
                  >
                    Raise Children Grounded
                  </motion.span>
                </span>
                <span className="mask-reveal text-primary italic">
                  <motion.span
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
                    className="block"
                  >
                    in Spiritual Wisdom.
                  </motion.span>
                </span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-base sm:text-lg font-semibold text-primary mb-6 text-left"
              >
                A guided system for emotional calm, connection and God-conscious family routines.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-base sm:text-lg lg:text-xl text-muted-foreground font-light leading-relaxed max-w-xl mb-10"
              >
                What if parenting could feel calmer, more connected and more purposeful—instead of constantly feeling overwhelmed, guilty or unsure? 
                <br /><br />
                I created God Conscious Parenting Hub to support mothers in building deeper connections, strengthening childhood bonds, creating healthier family relationships, and responding with calm instead of stress.
              </motion.p>

              {/* Interactive action controls */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto"
              >
                <a
                  href="https://learning.godconsciousparenting.com/web/checkout/6a9d08752dcaa6295db3b498"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-primary-cta"
                  className="px-8 py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase text-center flex items-center justify-center gap-2 group shadow-lg shadow-primary/10 hover:shadow-primary/20 transition-all duration-300 cursor-pointer"
                >
                  Join Free Master Class
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </motion.div>

            </div>

            {/* Right Media Column - 3D Interactive Parallax Frame */}
            <div className="lg:col-span-5 flex justify-center relative">
              <div className="absolute -top-12 -left-12 w-64 h-64 bg-secondary/15 rounded-full blur-3xl -z-10 pointer-events-none"></div>
              <div className="absolute -bottom-12 -right-12 w-64 h-64 bg-primary/10 rounded-full blur-3xl -z-10 pointer-events-none"></div>
              
              <div
                onMouseMove={handleHeroMouseMove}
                onMouseLeave={handleHeroMouseLeave}
                className="relative w-full max-w-sm sm:max-w-md aspect-[4/5] rounded-[2.5rem] bg-accent p-3 border border-foreground/5 shadow-2xl transition-all duration-300 ease-out cursor-pointer"
                style={{ transformStyle: "preserve-3d" }}
              >
                <div
                  ref={heroCardRef}
                  className="w-full h-full rounded-[2.2rem] overflow-hidden relative shadow-inner transition-transform duration-300 ease-out"
                >
                  <img
                    src={IMAGES.hero}
                    alt="Platinum Membership Box Bundle representing a Mother's Transformation"
                    className="w-full h-full object-cover select-none scale-105 group-hover:scale-100 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* SLOW EDITORIAL SLIDING MARQUEE */}
        <div className="border-y border-foreground/5 bg-accent/40 py-6 overflow-hidden relative select-none">
          <div className="animate-marquee whitespace-nowrap flex items-center gap-16 font-serif text-lg sm:text-xl lg:text-2xl font-light text-foreground/80 tracking-wider">
            <span>• SPIRITUAL ATTACHMENT</span>
            <span>• MINDFUL CONNECTION</span>
            <span>• ANCIENT WISDOM</span>
            <span>• GENTLE GUIDANCE</span>
            <span>• FAMILY RITUALS</span>
            <span>• CONSCIOUS FAMILIES</span>
            <span>• SPIRITUAL CO-REGULATION</span>
            <span>• NURTURE THE SOUL</span>
            {/* Repeat for seamless infinite looping */}
            <span>• SPIRITUAL ATTACHMENT</span>
            <span>• MINDFUL CONNECTION</span>
            <span>• ANCIENT WISDOM</span>
            <span>• GENTLE GUIDANCE</span>
            <span>• FAMILY RITUALS</span>
            <span>• CONSCIOUS FAMILIES</span>
            <span>• SPIRITUAL CO-REGULATION</span>
            <span>• NURTURE THE SOUL</span>
          </div>
        </div>

        {/* NUMBERED MANIFESTO CHAPTERS SECTION */}
        <section id="manifesto" className="py-24 bg-background border-b border-foreground/5">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            
            <div className="max-w-2xl text-left mb-16 sm:mb-24">
              <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">CONSCIOUS MANIFESTO</h2>
              <p className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
                Four Chapters of Conscious Spiritual Parenting
              </p>
              <p className="text-muted-foreground mt-4 font-light">
                Discover the framework that transitions your family from reactive conditioning to deep, spiritual connection and peace.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              
              {/* Chapter 01 */}
              <motion.div
                whileHover={{ y: -6 }}
                onMouseMove={handleSpotlightMouseMove}
                className="spotlight-card bg-accent rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between h-[360px] transition-all duration-300"
                data-testid="pillar-card-0"
              >
                <div>
                  <span className="font-serif text-4xl italic text-primary/80 font-bold">01</span>
                  <h3 className="font-serif text-2xl font-bold text-foreground mt-4 mb-2">Spiritual Attachment</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Develop absolute emotional co-regulation and presence. Control your own reactivity to become a steady, peaceful anchor for your child.
                  </p>
                </div>
                <div className="w-full h-24 rounded-2xl overflow-hidden mt-4">
                  <img src={IMAGES.manifesto1} alt="Person in silent contemplation looking at a mountain sunrise" className="w-full h-full object-cover" />
                </div>
              </motion.div>

              {/* Chapter 02 */}
              <motion.div
                whileHover={{ y: -6 }}
                onMouseMove={handleSpotlightMouseMove}
                className="spotlight-card bg-accent rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between h-[360px] transition-all duration-300"
                data-testid="pillar-card-1"
              >
                <div>
                  <span className="font-serif text-4xl italic text-primary/80 font-bold">02</span>
                  <h3 className="font-serif text-2xl font-bold text-foreground mt-4 mb-2">Family Rituals</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Design beautiful, grounding daily rituals and morning gratitude practices that compound into long-term peace and deep family connection.
                  </p>
                </div>
                <div className="w-full h-24 rounded-2xl overflow-hidden mt-4">
                  <img src={IMAGES.manifesto2} alt="Sunlight streaming onto a stack of books and a morning tea" className="w-full h-full object-cover" />
                </div>
              </motion.div>

              {/* Chapter 03 */}
              <motion.div
                whileHover={{ y: -6 }}
                onMouseMove={handleSpotlightMouseMove}
                className="spotlight-card bg-accent rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between h-[360px] transition-all duration-300"
                data-testid="pillar-card-2"
              >
                <div>
                  <span className="font-serif text-4xl italic text-primary/80 font-bold">03</span>
                  <h3 className="font-serif text-2xl font-bold text-foreground mt-4 mb-2">Ancient & Spiritual Wisdom</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Master the art of gentle communication. Share ancient spiritual wisdom and mindfulness through simple, heartfelt storytelling.
                  </p>
                </div>
                <div className="w-full h-24 rounded-2xl overflow-hidden mt-4">
                  <img src={IMAGES.manifesto3} alt="A hand writing in an open journal with elegant handwriting" className="w-full h-full object-cover" />
                </div>
              </motion.div>

              {/* Chapter 04 */}
              <motion.div
                whileHover={{ y: -6 }}
                onMouseMove={handleSpotlightMouseMove}
                className="spotlight-card bg-accent rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between h-[360px] transition-all duration-300"
                data-testid="pillar-card-3"
              >
                <div>
                  <span className="font-serif text-4xl italic text-primary/80 font-bold">04</span>
                  <h3 className="font-serif text-2xl font-bold text-foreground mt-4 mb-2">Conscious Co-Regulation</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Foster your child's innate spiritual nature and emotional intelligence without sacrificing joy, stability, or peace.
                  </p>
                </div>
                <div className="w-full h-24 rounded-2xl overflow-hidden mt-4">
                  <img src={IMAGES.manifesto4} alt="Minimal forest scene with dense green pine trees and low hanging fog" className="w-full h-full object-cover" />
                </div>
              </motion.div>

            </div>

          </div>
        </section>

        {/* INTERACTIVE SELF-LEADERSHIP ARCHETYPE QUIZ SECTION */}
        <section id="quiz" className="py-24 bg-accent/40 border-b border-foreground/5 relative">
          <div className="max-w-4xl mx-auto px-6 sm:px-8">
            
            <div className="text-center mb-16">
              <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">INTERACTIVE DIAGNOSTIC</h2>
              <p className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
                Reveal Your Self-Leadership Archetype
              </p>
              <p className="text-muted-foreground max-w-xl mx-auto mt-4 font-light">
                Discover your subconscious operating system and receive curated, actionable recommendations instantly.
              </p>
            </div>

            {/* Quiz Board Container */}
            <div data-testid="quiz-card" className="bg-background rounded-[2.5rem] p-8 sm:p-12 border border-foreground/5 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
              
              <AnimatePresence mode="wait">
                
                {/* Step 0: Welcome / Introduction */}
                {quizStep === 0 && (
                  <motion.div
                    key="step-0"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="text-center flex flex-col items-center py-6"
                  >
                    <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center mb-6 border border-foreground/5 shadow">
                      <Sparkles className="w-8 h-8 text-primary animate-pulse" />
                    </div>
                    <h3 className="font-serif text-3xl font-light text-foreground mb-4">Are you reacting, planning, or leading?</h3>
                    <p className="text-muted-foreground font-light max-w-md mb-8 leading-relaxed">
                      This brief 3-question evaluation identifies your current habits, composure capacity, and provides a direct, custom learning pathway.
                    </p>
                    <button
                      onClick={() => setQuizStep(1)}
                      data-testid="quiz-option-0"
                      className="px-8 py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase transition-all duration-300 flex items-center gap-2"
                    >
                      Begin Evaluation <ArrowRight className="w-4 h-4" />
                    </button>
                  </motion.div>
                )}

                {/* Steps 1, 2, 3: Active Questions */}
                {quizStep > 0 && quizStep <= 3 && (
                  <motion.div
                    key={`step-${quizStep}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="text-left"
                  >
                    {/* Progress Indicator */}
                    <div className="flex items-center justify-between mb-8">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">Question 0{quizStep} of 03</span>
                      <div className="w-32 bg-accent h-1.5 rounded-full overflow-hidden">
                        <div className="bg-primary h-full transition-all duration-300" style={{ width: `${(quizStep / 3) * 100}%` }}></div>
                      </div>
                    </div>

                    <h3 className="font-serif text-2xl sm:text-3xl text-foreground font-light leading-snug mb-8">
                      {quizQuestions[quizStep - 1].q}
                    </h3>

                    <div className="flex flex-col gap-4">
                      {quizQuestions[quizStep - 1].options.map((opt, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleQuizAnswer(opt.value)}
                          data-testid={`quiz-option-${idx}`}
                          className="w-full p-6 rounded-2xl bg-accent hover:bg-accent/80 border border-foreground/5 hover:border-primary/20 text-left flex items-center justify-between group transition-all duration-300 shadow-sm"
                        >
                          <span className="text-sm sm:text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                            {opt.text}
                          </span>
                          <div className="w-8 h-8 rounded-full bg-background border border-foreground/10 group-hover:bg-primary group-hover:border-primary flex items-center justify-center transition-all duration-300">
                            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary-foreground transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Diagnostic Result Output */}
                {quizStep === 4 && quizResult && (
                  <motion.div
                    key="step-4"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-left py-4"
                  >
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
                      <Award className="w-4 h-4 text-primary" />
                      <span className="text-xs font-bold text-primary uppercase tracking-wider">{quizResult.badge}</span>
                    </div>

                    <h3 className="font-serif text-4xl sm:text-5xl font-light text-foreground mb-4">
                      {quizResult.title}
                    </h3>

                    <p className="text-base sm:text-lg text-muted-foreground font-light leading-relaxed mb-6">
                      {quizResult.desc}
                    </p>

                    <div className="p-6 rounded-2xl bg-accent border-l-4 border-primary italic font-serif text-lg text-foreground mb-8">
                      &ldquo;{quizResult.quote}&rdquo;
                    </div>

                    <div className="p-6 rounded-2xl bg-accent border border-foreground/5 mb-8 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="w-5 h-5 text-primary" />
                      </div>
                      <div className="text-left">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Actionable Priority</h4>
                        <p className="text-sm font-semibold text-foreground mt-0.5">{quizResult.advise}</p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <a
                        href="#community"
                        className="w-full sm:w-auto px-8 py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase text-center transition-all duration-300"
                      >
                        Enroll with your Archetype
                      </a>
                      <button
                        onClick={restartQuiz}
                        data-testid="quiz-restart-button"
                        className="w-full sm:w-auto px-8 py-4 rounded-full bg-accent hover:bg-accent/80 text-foreground font-bold text-xs tracking-wider uppercase border border-foreground/5 flex items-center justify-center gap-2 transition-all duration-300"
                      >
                        <RefreshCw className="w-4 h-4 text-muted-foreground" /> Restart Diagnostic
                      </button>
                    </div>
                  </motion.div>
                )}

              </AnimatePresence>

            </div>

          </div>
        </section>

        {/* PLATINUM MEMBERSHIP SECTION */}
        <section id="membership" className="py-24 bg-accent/20 border-b border-foreground/5 relative">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">PLATINUM EXPERIENCE</h2>
              <h3 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
                Our Family-Transformation Journey
              </h3>
              <p className="text-muted-foreground mt-4 font-light leading-relaxed">
                My Platinum Membership brings together my complete family-transformation journey. Come learn, practise, connect and grow with mothers who are walking the same journey.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              
              {/* Journey 1 */}
              <motion.div
                whileHover={{ y: -6 }}
                className="bg-background rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300"
                data-testid="membership-journey-card-0"
              >
                <div>
                  <div className="w-12 h-10 rounded-xl bg-accent flex items-center justify-center mb-6 border border-foreground/5">
                    <span className="text-xl">🧘</span>
                  </div>
                  <h4 className="font-serif text-xl font-bold text-foreground mb-3">30-Day Spiritual Grounding Journey</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Deepen your connection with God, establish grounding daily gratitude, and center your spirit.
                  </p>
                </div>
              </motion.div>

              {/* Journey 2 */}
              <motion.div
                whileHover={{ y: -6 }}
                className="bg-background rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300"
                data-testid="membership-journey-card-1"
              >
                <div>
                  <div className="w-12 h-10 rounded-xl bg-accent flex items-center justify-center mb-6 border border-foreground/5">
                    <span className="text-xl">❤️</span>
                  </div>
                  <h4 className="font-serif text-xl font-bold text-foreground mb-3">30-Day Sacred Relationship Journey</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Build a harmonious, loving foundation with your partner and align your family's vision.
                  </p>
                </div>
              </motion.div>

              {/* Journey 3 */}
              <motion.div
                whileHover={{ y: -6 }}
                className="bg-background rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300"
                data-testid="membership-journey-card-2"
              >
                <div>
                  <div className="w-12 h-10 rounded-xl bg-accent flex items-center justify-center mb-6 border border-foreground/5">
                    <span className="text-xl">💞</span>
                  </div>
                  <h4 className="font-serif text-xl font-bold text-foreground mb-3">Relationship Rebirth System</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Reconstruct communication patterns, heal conditioning, and cultivate mindful coregulation.
                  </p>
                </div>
              </motion.div>

              {/* Journey 4 */}
              <motion.div
                whileHover={{ y: -6 }}
                className="bg-background rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300"
                data-testid="membership-journey-card-3"
              >
                <div>
                  <div className="w-12 h-10 rounded-xl bg-accent flex items-center justify-center mb-6 border border-foreground/5">
                    <span className="text-xl">🤰</span>
                  </div>
                  <h4 className="font-serif text-xl font-bold text-foreground mb-3">Best Pregnancy Plan</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Nurture your child's spiritual nature and well-being from the very beginning of life's sacred journey.
                  </p>
                </div>
              </motion.div>

              {/* Journey 5 */}
              <motion.div
                whileHover={{ y: -6 }}
                className="bg-background rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300"
                data-testid="membership-journey-card-4"
              >
                <div>
                  <div className="w-12 h-10 rounded-xl bg-accent flex items-center justify-center mb-6 border border-foreground/5">
                    <span className="text-xl">🌸</span>
                  </div>
                  <h4 className="font-serif text-xl font-bold text-foreground mb-3">Self Connection</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Ground your identity, cultivate inner calm, and transcend mother fatigue through self-awareness.
                  </p>
                </div>
              </motion.div>

              {/* Journey 6 */}
              <motion.div
                whileHover={{ y: -6 }}
                className="bg-background rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300"
                data-testid="membership-journey-card-5"
              >
                <div>
                  <div className="w-12 h-10 rounded-xl bg-accent flex items-center justify-center mb-6 border border-foreground/5">
                    <span className="text-xl">👩‍👧</span>
                  </div>
                  <h4 className="font-serif text-xl font-bold text-foreground mb-3">Child Connection</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Strengthen the invisible emotional attachment bond with your children through presence and play.
                  </p>
                </div>
              </motion.div>

              {/* Journey 7 */}
              <motion.div
                whileHover={{ y: -6 }}
                className="bg-background rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between shadow-sm hover:shadow-md transition-all duration-300 md:col-span-2 lg:col-span-3 lg:max-w-md lg:mx-auto"
                data-testid="membership-journey-card-6"
              >
                <div>
                  <div className="w-12 h-10 rounded-xl bg-accent flex items-center justify-center mb-6 border border-foreground/5">
                    <span className="text-xl">👨‍👩‍👧‍👦</span>
                  </div>
                  <h4 className="font-serif text-xl font-bold text-foreground mb-3">Family Connection</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Create rich, grounding daily rituals and values-driven routines that unite the entire family.
                  </p>
                </div>
              </motion.div>

            </div>

            {/* Premium CTA Box */}
            <div className="bg-background rounded-[2.5rem] p-8 sm:p-12 border border-foreground/5 shadow-xl text-center max-w-3xl mx-auto relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
              
              <span className="text-xs font-bold text-primary uppercase tracking-widest mb-3 inline-block">Tulip Pathway</span>
              <h4 className="font-serif text-3xl sm:text-4xl font-light text-foreground mb-4">
                Calm Mother. Connected Family. God-Conscious Child. Better World.
              </h4>
              <p className="text-muted-foreground font-light max-w-md mx-auto mb-8 leading-relaxed">
                You don't have to figure out everything alone. Come learn, practise, connect and grow in our premier master container.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://learning.godconsciousparenting.com/web/checkout/6a80ae6a63b86bc1de38388f"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="explore-platinum-cta"
                  className="px-8 py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase text-center flex items-center justify-center gap-2 group shadow-lg shadow-primary/10 hover:shadow-primary/20 transition-all duration-300"
                >
                  Explore Platinum Membership
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="https://learning.godconsciousparenting.com/web/checkout/6a9d08752dcaa6295db3b498"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="platinum-secondary-join-button"
                  className="px-8 py-4 rounded-full bg-accent hover:bg-accent/80 text-foreground font-bold text-xs tracking-wider uppercase text-center transition-all duration-300 border border-foreground/5 cursor-pointer flex items-center justify-center"
                >
                  Join Free Master Class
                </a>
              </div>
            </div>

          </div>
        </section>

        {/* AUTHOR SPOTLIGHT PROFILE SECTION */}
        <section id="about" className="py-24 bg-background border-b border-foreground/5">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              
              {/* Profile Photo with spotlight clipping */}
              <div className="lg:col-span-5 flex justify-center">
                <div className="relative w-full max-w-sm aspect-[4/5] rounded-[2.5rem] bg-accent p-3 border border-foreground/5 shadow-xl">
                  <div className="w-full h-full rounded-[2.2rem] overflow-hidden relative">
                    <img src={IMAGES.author} alt="Swayamprava looking serene and smiling gently" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent flex flex-col justify-end p-8 text-left">
                      <p className="text-xs font-bold uppercase tracking-widest text-accent mb-1">FOUNDER & GUIDE</p>
                      <h3 className="font-serif text-3xl text-background font-bold">Swayamprava</h3>
                      <p className="text-xs text-accent/80 mt-1">Conscious Parent Educator</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bio Copy */}
              <div className="lg:col-span-7 text-left">
                <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">THE COMPANIONSHIP</h2>
                <h3 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground mb-6">
                  Walk the Path of Conscious Divine Parenting
                </h3>
                
                <p className="text-muted-foreground font-light leading-relaxed mb-6">
                  Swayamprava is a spiritual parent educator, dedicated to timeless wisdom, spiritual attachment practices, and modern conscious parenting. Her work bridges ancient wisdom with developmental psychology to help families connect deeply.
                </p>

                <p className="text-muted-foreground font-light leading-relaxed mb-8">
                  She founded the God Conscious Parenting Hub to provide a high-caliber container for mindful families to raise children grounded in peace, absolute focus, and spiritual strength.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center border border-foreground/5">
                      <Award className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-serif text-lg font-bold text-foreground">Ancient & Modern</h4>
                      <p className="text-xs text-muted-foreground mt-1">Parenting methods blending spiritual philosophy with developmental psychology.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center border border-foreground/5">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-serif text-lg font-bold text-foreground">Satsang & Community</h4>
                      <p className="text-xs text-muted-foreground mt-1">Belong to a spiritual peer group of dedicated parents.</p>
                    </div>
                  </div>
                </div>

                <a
                  href="https://learning.godconsciousparenting.com/web/checkout/6a9d08752dcaa6295db3b498"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="spotlight-join-button"
                  className="px-8 py-4 rounded-full bg-foreground hover:bg-foreground/90 text-background font-bold text-xs tracking-wider uppercase inline-flex items-center justify-center gap-2 hover:gap-3 transition-all duration-300 cursor-pointer"
                >
                  Join Free Master Class <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>

            </div>
          </div>
        </section>

        {/* WHATSAPP COMMUNITY SECTION */}
        <section id="community" className="py-24 bg-accent/40 border-b border-foreground/5 text-center">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            
            <div className="text-center mb-12">
              <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">COMMUNITY</h2>
              <h3 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
                Join Our Free Master Class
              </h3>
              <p className="text-muted-foreground max-w-xl mx-auto mt-4 font-light leading-relaxed">
                Gain immediate access to our exclusive Free Master Class, and receive practical parenting tools, spiritual practices, and gentle guidance directly from Swayamprava.
              </p>
            </div>

            <div className="bg-background rounded-[2.5rem] p-8 sm:p-12 border border-foreground/5 shadow-xl max-w-xl mx-auto text-left relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-extrabold uppercase tracking-widest px-6 py-2 rounded-bl-[1.5rem] shadow-sm">
                Active Group
              </div>

              <h4 className="font-serif text-2xl font-bold text-foreground mb-4">The Free Master Class</h4>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-8">
                Unlock our core developmental communication blueprints, self-regulation frameworks, and spiritual family practices designed for modern mothers.
              </p>

              {/* Guidelines checklist */}
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-sm text-foreground/95">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <span>100% Respectful, supportive, and mindful conversations</span>
                </li>
                <li className="flex items-center gap-3 text-sm text-foreground/95">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <span>Daily morning gratitude and evening coregulation routines</span>
                </li>
              </ul>

              <hr className="border-foreground/5 my-6" />

              {/* Join CTA button */}
              <a
                href="https://learning.godconsciousparenting.com/web/checkout/6a9d08752dcaa6295db3b498"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-join-button"
                className="w-full py-4 rounded-full font-bold text-xs tracking-wider uppercase text-center block transition-all duration-300 bg-primary hover:bg-primary/95 text-primary-foreground shadow-lg shadow-primary/15 cursor-pointer animate-pulse flex items-center justify-center"
              >
                Join Free Master Class
              </a>
            </div>

          </div>
        </section>

        {/* ACCORDION FAQ SECTION */}
        <section id="faq" className="py-24 bg-accent/40 border-b border-foreground/5">
          <div className="max-w-3xl mx-auto px-6 sm:px-8">
            
            <div className="text-center mb-16">
              <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">RESOLUTIONS</h2>
              <h3 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
                Inquiries & Insights
              </h3>
              <p className="text-muted-foreground mt-3 font-light">
                Everything you need to know about the structure, commitment, and alignment of the club.
              </p>
            </div>

            {/* Accordion List */}
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-background rounded-2xl border border-foreground/5 overflow-hidden shadow-sm">
                  <button
                    onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                    data-testid={`faq-trigger-${index}`}
                    className="w-full px-6 py-5 text-left flex items-center justify-between group transition-colors"
                  >
                    <span className="font-serif text-lg sm:text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                      {faq.q}
                    </span>
                    <motion.div
                      animate={{ rotate: faqOpen === index ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                      className="w-8 h-8 rounded-full bg-accent flex items-center justify-center"
                    >
                      <ChevronDown className="w-4 h-4 text-foreground" />
                    </motion.div>
                  </button>

                  <AnimatePresence initial={false}>
                    {faqOpen === index && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 text-sm sm:text-base text-muted-foreground font-light leading-relaxed border-t border-foreground/5 pt-4 text-left">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

          </div>
        </section>

      </main>

      {/* FOOTER SECTION */}
      <footer className="bg-foreground text-background py-16 px-6 sm:px-8 text-left relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 border-b border-background/10 pb-12 mb-12">
          
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center border border-background/10 overflow-hidden">
                <img src={IMAGES.logo} className="w-full h-full object-contain" alt="God Conscious Parenting Logo" />
              </div>
              <span className="font-serif text-2xl font-bold tracking-tight text-background">God Conscious Parenting Hub</span>
            </div>
            <p className="text-background/70 font-light leading-relaxed max-w-md mb-8">
              A premium spiritual mastermind and conscious parenting framework designed to transition your family from reactive parenting to peaceful, mindful connection. Founded by Swayamprava, God Conscious Parenting Hub supports mothers worldwide.
            </p>
            <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest text-background/50">
              <span className="flex items-center gap-1.5"><Globe className="w-4 h-4" /> Based Globally</span>
              <span>•</span>
              <span>© 2026 God Conscious Parenting Hub</span>
            </div>
          </div>

          {/* Quick Newsletter form */}
          <div className="lg:col-span-6 flex flex-col justify-center">
            <h4 className="font-serif text-xl text-background font-semibold mb-3">Receive weekly mindful parenting insights</h4>
            <p className="text-background/60 text-sm font-light mb-6">No noise, no spam. Raw and refined practices directly to your inbox.</p>
            
            {!footerSubmitted ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (footerName && footerEmail) {
                    setFooterSubmitted(true);
                  }
                }}
                className="space-y-3 max-w-md text-left"
              >
                <div className="grid grid-cols-1 gap-3">
                  <input
                    type="text"
                    required
                    value={footerName}
                    onChange={(e) => setFooterName(e.target.value)}
                    placeholder="Enter your full name"
                    data-testid="footer-newsletter-name"
                    className="px-5 py-3.5 rounded-full bg-background/5 border border-background/10 text-background placeholder:text-background/40 focus:outline-none focus:border-primary transition-colors text-sm w-full"
                  />
                  <input
                    type="email"
                    required
                    value={footerEmail}
                    onChange={(e) => setFooterEmail(e.target.value)}
                    placeholder="Enter your email address"
                    data-testid="footer-newsletter-input"
                    className="px-5 py-3.5 rounded-full bg-background/5 border border-background/10 text-background placeholder:text-background/40 focus:outline-none focus:border-primary transition-colors text-sm w-full"
                  />
                  <input
                    type="tel"
                    value={footerPhone}
                    onChange={(e) => setFooterPhone(e.target.value)}
                    placeholder="Enter your phone number (optional)"
                    data-testid="footer-newsletter-phone"
                    className="px-5 py-3.5 rounded-full bg-background/5 border border-background/10 text-background placeholder:text-background/40 focus:outline-none focus:border-primary transition-colors text-sm w-full"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="footer-newsletter-submit"
                  className="w-full px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-2 hover:bg-primary/95 transition-all duration-300"
                >
                  Subscribe <Send className="w-3.5 h-3.5" />
                </button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 rounded-2xl bg-secondary/10 border border-secondary/20 text-center flex flex-col items-center max-w-md"
              >
                <Check className="w-8 h-8 text-primary mb-2 animate-bounce" />
                <h5 className="font-serif text-lg font-bold text-background">Subscription Active</h5>
                <p className="text-xs text-background/60 mt-1">Thank you for joining our spiritual parenting circle!</p>
              </motion.div>
            )}
          </div>

        </div>

        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between text-xs text-background/45 font-medium tracking-wide">
          <div className="flex items-center gap-6 mb-4 sm:mb-0">
            <a href="#home" className="hover:text-background transition-colors">Privacy Policy</a>
            <a href="#home" className="hover:text-background transition-colors">Terms of Use</a>
            <a href="#home" className="hover:text-background transition-colors">Cookie Configurations</a>
          </div>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="scroll-to-top-button"
            className="px-4 py-2 rounded-full border border-background/10 hover:border-background text-background/60 hover:text-background flex items-center gap-2 transition-all duration-300"
          >
            Back to Top <ChevronDown className="w-4 h-4 rotate-180" />
          </button>
        </div>
      </footer>

      {/* 5-SECOND DELAYED COMPLIMENTARY EBOOK POPUP DIALOG */}
      <AnimatePresence>
        {showEbook && (
          <div data-testid="ebook-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-foreground/50 backdrop-blur-md">
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-background rounded-[2.5rem] max-w-2xl w-full border border-foreground/5 overflow-hidden shadow-2xl relative grid grid-cols-1 md:grid-cols-12"
            >
              {/* Close Button */}
              <button
                onClick={closeEbook}
                data-testid="ebook-close-button"
                className="absolute top-4 right-4 p-2.5 rounded-full bg-background border border-foreground/5 text-foreground hover:text-primary hover:shadow transition-all duration-300 z-10"
                aria-label="Close dialogue"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Cover Column */}
              <div className="md:col-span-5 bg-accent relative overflow-hidden hidden md:block">
                <img src={IMAGES.ebook} alt="Open vintage book on wooden table in warm atmospheric room" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-primary/10"></div>
              </div>

              {/* Form Content Column */}
              <div className="md:col-span-7 p-8 sm:p-10 text-left flex flex-col justify-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4 w-fit">
                  <BookOpen className="w-3.5 h-3.5 text-primary" />
                  <span className="text-[10px] font-extrabold text-primary uppercase tracking-wider">Complimentary Copy</span>
                </div>

                <h4 className="font-serif text-3xl font-light text-foreground mb-3 leading-tight">
                  The Art of Conscious Parenting
                </h4>
                
                <p className="text-muted-foreground text-sm font-light leading-relaxed mb-6">
                  Unlock our 40-page blueprint containing spiritual child rituals, gentle coregulation guides, and mindful parenting playbooks.
                </p>

                <AnimatePresence mode="wait">
                  {!ebookSubmitted ? (
                    <motion.form
                      key="form"
                      onSubmit={handleEbookSubmit}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-3"
                    >
                      <input
                        type="text"
                        required
                        value={ebookName}
                        onChange={(e) => setEbookName(e.target.value)}
                        placeholder="Enter your full name"
                        data-testid="ebook-name-input"
                        className="px-5 py-3.5 rounded-full bg-accent border border-foreground/5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors text-sm w-full"
                      />
                      <input
                        type="email"
                        required
                        value={ebookEmail}
                        onChange={(e) => setEbookEmail(e.target.value)}
                        placeholder="Enter your primary email"
                        data-testid="ebook-email-input"
                        className="px-5 py-3.5 rounded-full bg-accent border border-foreground/5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors text-sm w-full"
                      />
                      <input
                        type="tel"
                        value={ebookPhone}
                        onChange={(e) => setEbookPhone(e.target.value)}
                        placeholder="Enter your phone number (optional)"
                        data-testid="ebook-phone-input"
                        className="px-5 py-3.5 rounded-full bg-accent border border-foreground/5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors text-sm w-full"
                      />
                      <button
                        type="submit"
                        data-testid="ebook-submit-button"
                        className="w-full py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-2 shadow transition-colors"
                      >
                        Claim Complimentary Blueprint <ArrowRight className="w-4 h-4" />
                      </button>
                    </motion.form>
                  ) : (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      data-testid="ebook-success-message"
                      className="p-6 rounded-2xl bg-secondary/10 border border-secondary/20 text-center flex flex-col items-center"
                    >
                      <Check className="w-8 h-8 text-primary mb-2 animate-bounce" />
                      <h5 className="font-serif text-lg font-bold text-foreground">Spiritual Blueprint Sent</h5>
                      <p className="text-xs text-muted-foreground mt-1">Please verify your primary email shortly.</p>
                    </motion.div>
                  )}
                </AnimatePresence>
                
              </div>

            </motion.div>

          </div>
        )}
      </AnimatePresence>



    </div>
  );
}
